import { cn, CustomTooltip, IntakeFormLinkModalContent, TabItem, TabsContainer } from '@client';
import { SheetModal } from './SheetModal';
import { EmailTagInput } from './EmailTagInput';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Info, Send } from 'lucide-react';
import { FormProvider } from 'react-hook-form';
import { PreviewAndTestEmail } from './PreviewAndTestEmail';
import { OptionalSettingsFields } from './OptionalSettingsFields';
import { useInviteClientModal } from '@client/hooks/useClientInvite';
import { Switch } from './ui/switch';

interface InviteClientsModalProps {
  open: boolean;
  onClose: () => void;
  maxEmails?: number;
  defaultTab?: 'email' | 'link';
  shouldPrepopulateEmail?: boolean;
}

export function InviteClientModal({
  open,
  onClose,
  maxEmails = 35,
  defaultTab = 'email',
  shouldPrepopulateEmail = false,
}: InviteClientsModalProps) {
  const {
    advisor,
    emails,
    setEmails,
    complianceConfirmed,
    setComplianceConfirmed,
    selectedTab,
    setSelectedTab,
    showPreview,
    setShowPreview,
    isSingleInvite,
    isBulkInvite,
    uniqueValidEmails,
    isLoading,
    isComplete,
    isError,
    errorMessage,
    handleSubmit,
    handleSendTestEmail,
    handleClose,
    methods,
    resetState,
    setValue,
    formData,
  } = useInviteClientModal({
    open,
    defaultTab,
    shouldPrepopulateEmail,
    maxEmails,
    onClose,
  });

  // Build the tabs
  const tabs: TabItem[] = [
    {
      value: 'email',
      label: 'Invite via Email',
      content: (
        <>
          <EmailTagInput
            onChange={setEmails}
            initialEmails={emails}
            maxEmails={maxEmails}
          />

          <PreviewAndTestEmail
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            handleSendTestEmail={handleSendTestEmail}
            isLoading={isLoading}
            clientFirstName={formData.clientFirstName || ''}
            clientLastName={formData.clientLastName || ''}
            advisorFirstName={advisor.advisorFirstName}
            organizationDisplayName={advisor.organizationDisplayName ?? ''}
            advisorProvidedBody={formData.advisorProvidedBody}
            onChangeAdvisorProvidedBody={val =>
              setValue('advisorProvidedBody', val, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
            onChangeClientFirstName={val =>
              setValue('clientFirstName', val, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
            onChangeClientLastName={val =>
              setValue('clientLastName', val, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              })
            }
            showClientNameFields={isSingleInvite}
          />

          <OptionalSettingsFields />

          {isBulkInvite && (
            <div className="mt-4 flex items-start gap-3">
              <Checkbox
                id="compliance"
                checked={complianceConfirmed}
                onCheckedChange={checked => setComplianceConfirmed(!!checked)}
              />
              <Label
                htmlFor="compliance"
                className="text-sm italic text-gray-600"
              >
                I confirm that I have either obtained compliance approval or understand this message
                may require compliance review. I acknowledge that sending this email is my
                responsibility.
              </Label>
            </div>
          )}
        </>
      ),
    },
    {
      value: 'link',
      label: 'Invite via Link',
      content: <IntakeFormLinkModalContent className="my-8" />,
    },
  ];

  return (
    <SheetModal
      open={open}
      onClose={handleClose}
      title="Invite Clients"
      subTitle=""
      isLoading={isLoading}
      errorMessage={isError ? errorMessage || 'Failed to send invitation(s)' : ''}
      showSuccess={isComplete}
      contentTestId="invite-client-modal-content"
      successMessage={
        isBulkInvite
          ? 'Your invitations have been sent successfully. Your clients will receive an email shortly.'
          : 'Invitation successfully sent. Your client will receive an email shortly.'
      }
      primaryButton={
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={resetState}
            disabled={isLoading}
          >
            Clear
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              uniqueValidEmails.length === 0 || (isBulkInvite && !complianceConfirmed) || isLoading
            }
            className="bg-primary hover:bg-primary-hover text-white"
            data-testid="send-invitation-button"
          >
            <Send className="mr-2 h-4 w-4" />
            {isLoading
              ? 'Sending...'
              : `Send ${uniqueValidEmails.length} Invite${uniqueValidEmails.length > 1 ? 's' : ''}`}
          </Button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <div className="mt-2 flex flex-col gap-4">
          <TabsContainer
            tabs={tabs}
            defaultValue={defaultTab}
            value={selectedTab}
            onValueChange={setSelectedTab}
          />
        </div>
      </FormProvider>
    </SheetModal>
  );
}

export function ShareResultsSwitch({
  value,
  onChange,
  className,
}: {
  value: boolean;
  onChange: (c: boolean) => void;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <label className="text-primary-dark flex items-baseline gap-2 text-sm font-medium">
        <span>Share Results with Client</span>
        <CustomTooltip
          content={
            <div className="text-sm font-normal">
              <p>Give the client direct access to view their results.</p>
            </div>
          }
        >
          <Info className="text-primary-500 h-4 w-4" />
        </CustomTooltip>
      </label>
      <Switch
        checked={value}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-primary"
      />
    </div>
  );
}
